import CoreBase from '../../../services/engines/CoreBase';
import userJournalService from '../../../services/UserJournalService';
import { BOARD_FORMAT, BOARD_ITEM_LIMIT, BOARD_STATE_ACTIVE, BOARD_STATE_COMPLETED, DEFAULT_BOARD_LANES, LEGACY_LANES } from './boardConst';

// Raw I/O + mapping of boards and board items, no caching/notify (Engine's job)
class BoardFetcher extends CoreBase {
    _name = 'BoardFetcher';

    /**
     * @returns {Promise<object[]>} active boards (notes with format componentboard_data)
     */
    fetchBoards = async () => {
        try {
            const response = await userJournalService.getJournals('', '', '', '', BOARD_FORMAT);
            return this._items(response).filter(this.isActive).map(this.mapBoard);
        } catch (error) {
            this.logError('fetchBoards', error.message);
            return [];
        }
    };

    /**
     * @param {string} boardRk
     * @returns {Promise<object[]>} active + completed items linked to the board via parentRK
     */
    fetchItems = async (boardRk) => {
        try {
            const response = await userJournalService.getJournals('', '', '', boardRk, '', BOARD_ITEM_LIMIT);
            return this._items(response).filter(this.isOnBoard).map(this.mapItem);
        } catch (error) {
            this.logError('fetchItems', error.message);
            return [];
        }
    };

    /**
     * BE has no stateId filter on this endpoint — also used for SignalR pushes.
     * @param {object} journal
     * @returns {boolean}
     */
    isActive = (journal) => (journal.stateId ?? BOARD_STATE_ACTIVE) === BOARD_STATE_ACTIVE;

    /**
     * Board items: active and completed (completed go to the Done lane), hidden/deleted never.
     * @param {object} journal
     * @returns {boolean}
     */
    isOnBoard = (journal) => [BOARD_STATE_ACTIVE, BOARD_STATE_COMPLETED].includes(journal.stateId ?? BOARD_STATE_ACTIVE);

    _items = (response) => response?.data?.items ?? [];

    // raw keeps the note fields BoardSaver needs for the upsert — public, also used for SignalR pushes
    mapBoard = (item) => ({
        rk: item.rk,
        title: item.title ?? '',
        lanes: this._lanes(this._parse(item.body)),
        raw: { rk: item.rk, title: item.title, format: item.format, service: item.service, type: item.type, cat: item.cat, stage: item.stage, stateId: item.stateId ?? BOARD_STATE_ACTIVE },
    });

    // new format as-is, old format (string ids + hidden) converted, nothing usable → defaults
    _lanes = (cfg) => {
        if (!Array.isArray(cfg.lanes) || cfg.lanes.length === 0) return DEFAULT_BOARD_LANES;
        if (typeof cfg.lanes[0] !== 'string') return cfg.lanes;
        const hidden = cfg.hidden ?? [];
        return cfg.lanes.filter((l) => LEGACY_LANES[l]).map((l) => ({ ...LEGACY_LANES[l], enabled: !hidden.includes(l) }));
    };

    // all fields PrioUpdater posts back are kept — public, also used for SignalR pushes
    mapItem = (item) => ({
        rk: item.rk,
        parentRK: item.parentRK ?? '',
        title: item.title ?? '',
        type: item.type ?? '',
        cat: item.cat ?? '',
        stage: item.stage ?? '',
        service: item.service ?? '',
        format: item.format ?? null,
        stateId: item.stateId ?? BOARD_STATE_ACTIVE,
        isPinned: item.isPinned ?? false,
        remark: item.remark ?? null,
        tid: item.tid ?? null,
        viewMode: item.viewMode ?? null,
        body: item.body ?? '',
        solution: item.solution ?? '',
        prioId: item.prioId ?? 0,
        importanceId: item.importanceId ?? 0,
        ems: item.ems ?? 0,
        updatedOn: item.updatedOn ?? 0,
    });

    _parse = (body) => {
        try {
            return JSON.parse(body || '{}');
        } catch (error) {
            this.logError('_parse', error.message);
            return {};
        }
    };
}

const boardFetcher = new BoardFetcher();
export default boardFetcher;
